import { matchPath } from "react-router-dom";
import { getAiTravelPlannerContent, getAiTravelPlannerJsonLd } from "../content/aiTravelPlanner";
import { getTravelPlanningGuide, getPlanningGuideJsonLd } from "../content/travelPlanningGuide";

const DEFAULT_TITLE = "TrackMe — планировщик путешествий и маршрутов";
const DEFAULT_DESCRIPTION =
    "TrackMe помогает планировать путешествия: создавать маршруты, вести бюджет, собирать вещи, хранить GPX-треки, заметки и участников поездки.";
const DEFAULT_ROBOTS = "index,follow";
const NOINDEX_ROBOTS = "noindex,follow";
const PUBLIC_ROUTES_SOCIAL_IMAGE =
    "https://static.trackme.su/images/seo/public-routes-social.jpg";

const NOINDEX_ROUTE_PATTERNS = [
    "/userlogin",
    "/register",
    "/createuser",
    "/signup",
    "/passwordreset",
    "/reset-password/:uid/:token",
    "/confirm-email/:token",
    "/auth/vk/callback",
    "/userlk/:userId/*",
    "/travel/shared/:token",
    "/error",
];

const resolveRobots = (pathname, configuredRobots) => {
    if (configuredRobots) return configuredRobots;

    const shouldNoIndex = NOINDEX_ROUTE_PATTERNS.some((path) =>
        Boolean(matchPath({ path, end: true, caseSensitive: false }, pathname))
    );

    return shouldNoIndex ? NOINDEX_ROBOTS : DEFAULT_ROBOTS;
};

const ORGANIZATION_JSON_LD = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://trackme.su/#organization",
    name: "TrackMe",
    url: "https://trackme.su/",
    logo: {
        "@type": "ImageObject",
        url: "https://trackme.su/favicon/trackme-round-512.png",
    },
    sameAs: ["https://vk.com/vktrackme"],
    contactPoint: [{
        "@type": "ContactPoint",
        contactType: "customer support",
        email: "support@trackme.su",
        availableLanguage: "Russian",
    }],
};

const WEBSITE_JSON_LD = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://trackme.su/#website",
    name: "TrackMe",
    url: "https://trackme.su/",
    inLanguage: "ru-RU",
    description: DEFAULT_DESCRIPTION,
    publisher: { "@id": "https://trackme.su/#organization" },
};

const buildBreadcrumbJsonLd = (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map(({ name, path }, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name,
        item: new URL(path, "https://trackme.su").toString(),
    })),
});

const CATALOG_BREADCRUMBS_JSON_LD = {
    ...buildBreadcrumbJsonLd([
        { name: "Главная", path: "/" },
        { name: "Каталог маршрутов", path: "/gotovye-marshruty/" },
    ]),
    "@id": "https://trackme.su/gotovye-marshruty/#breadcrumbs",
};

const CATALOG_COLLECTION_JSON_LD = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": "https://trackme.su/gotovye-marshruty/#collection",
    name: "Готовые маршруты для поездок",
    description: "Подборка публичных маршрутов с фильтрами по стране, типу поездки, дистанции и длительности.",
    url: "https://trackme.su/gotovye-marshruty/",
    inLanguage: "ru-RU",
};

const BOOKING_BREADCRUMBS_JSON_LD = buildBreadcrumbJsonLd([
    { name: "Главная", path: "/" },
    { name: "Сервисы для путешественников", path: "/booking/" },
]);

const PLANNING_FAQ_JSON_LD = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
        {
            "@type": "Question",
            name: "Можно ли начать планирование без готового маршрута?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Да. Создайте поездку с названием и примерными датами, а точки, этапы и GPX-треки добавляйте по мере подготовки.",
            },
        },
        {
            "@type": "Question",
            name: "Как учитывать расходы на путешествие?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "В плане можно задать общий бюджет и добавлять отдельные расходы по категориям, чтобы сравнивать плановую и фактическую сумму.",
            },
        },
        {
            "@type": "Question",
            name: "Можно ли загрузить GPX-трек?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Да. GPX-файлы можно хранить вместе с маршрутом и использовать для отображения пути и точек на карте.",
            },
        },
        {
            "@type": "Question",
            name: "Как планировать поездку вместе с друзьями?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Добавьте участников в план путешествия. Так маршрут, списки вещей, заметки и подготовка останутся доступны всей команде.",
            },
        },
        {
            "@type": "Question",
            name: "Можно ли взять за основу готовый маршрут?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Да. Откройте каталог публичных маршрутов, выберите подходящую идею и используйте её при подготовке собственной поездки.",
            },
        },
    ],
};

const buildFaqJsonLd = (items) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(({ question, answer }) => ({
        "@type": "Question",
        name: question,
        acceptedAnswer: { "@type": "Answer", text: answer },
    })),
});

const buildContentPageJsonLd = ({ type = "Article", title, description, path, faq }) => [
    {
        "@context": "https://schema.org",
        "@type": type,
        headline: title,
        name: title,
        description,
        url: `https://trackme.su${path}`,
        inLanguage: "ru-RU",
        publisher: { "@id": "https://trackme.su/#organization" },
    },
    buildBreadcrumbJsonLd([
        { name: "Главная", path: "/" },
        { name: "Планирование путешествий", path: "/planirovanie-puteshestviy/" },
        { name: title, path },
    ]),
    buildFaqJsonLd(faq),
];

const STATISTICS_TITLE = "Статистика путешествий: география, расходы и итоги | TrackMe";
const STATISTICS_DESCRIPTION = "Статистика путешествий в TrackMe: посещённые страны, города и места, расходы по категориям, перемещения, проживание и личные итоги поездок.";



const withSeoDefaults = (meta = {}, pathname = "/") => {
    const canonicalPath = meta.canonicalPath || "/";
    const openGraphOverrides = meta.openGraph === false ? null : meta.openGraph || {};
    const openGraph = openGraphOverrides
        ? {
            title: meta.title || DEFAULT_TITLE,
            description: meta.description || DEFAULT_DESCRIPTION,
            type: "website",
            siteName: "TrackMe",
            locale: "ru_RU",
            ...openGraphOverrides,
            url: openGraphOverrides.url || canonicalPath,
        }
        : false;
    const twitterOverrides = meta.twitter === false ? null : meta.twitter || {};
    const twitter = twitterOverrides
        ? {
            card: openGraph?.image ? "summary_large_image" : "summary",
            title: meta.title || DEFAULT_TITLE,
            description: meta.description || DEFAULT_DESCRIPTION,
            ...twitterOverrides,
        }
        : false;

    return {
        ...meta,
        canonicalPath,
        robots: resolveRobots(pathname, meta.robots),
        openGraph,
        twitter,
        jsonLd: meta.jsonLd || [],
    };
};

const LEGAL_DOC_META = {
    oferta: {
        title: "Договор-оферта | TrackMe",
        description: "Публичная оферта сервиса TrackMe: условия использования, оплаты и предоставления услуг.",
    },
    cookie: {
        title: "Политика Cookie | TrackMe",
        description: "Политика использования файлов Cookie в сервисе TrackMe.",
    },
    privacy: {
        title: "Политика конфиденциальности | TrackMe",
        description: "Политика конфиденциальности TrackMe: как обрабатываются и защищаются персональные данные.",
    },
    ua: {
        title: "Пользовательское соглашение | TrackMe",
        description: "Пользовательское соглашение сервиса TrackMe.",
    },
    pd: {
        title: "Политика обработки персональных данных | TrackMe",
        description: "Правила обработки персональных данных в сервисе TrackMe.",
    },
    consent: {
        title: "Согласие на обработку персональных данных | TrackMe",
        description: "Форма согласия на обработку персональных данных для пользователей TrackMe.",
    },
    refund: {
        title: "Политика возвратов | TrackMe",
        description: "Условия возврата и отмены оплаты в сервисе TrackMe.",
    },
};

const prettifySlug = (value) => {
    if (typeof value !== "string") return "";
    const trimmed = value.trim();
    if (!trimmed) return "";

    let decoded = trimmed;
    try {
        decoded = decodeURIComponent(trimmed);
    } catch {
        decoded = trimmed;
    }

    const cleaned = decoded
        .replace(/[-_]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    return cleaned
        .split(" ")
        .map((part) => (part ? `${part.charAt(0).toUpperCase()}${part.slice(1)}` : part))
        .join(" ");
};

const buildPublicTravelFallbackMeta = ({ pathname, params = {} }) => {
    const prettySlug = prettifySlug(params.travelSlug || params.travelIdentifier);
    const title = prettySlug
        ? `${prettySlug} — маршрут, как добраться и GPX | TrackMe`
        : "Маршрут путешествия — как добраться и GPX | TrackMe";
    const description = prettySlug
        ? `План маршрута ${prettySlug}: точки на карте, GPX-трек, снаряжение, файлы и заметки путешествия в TrackMe.`
        : "Детали публичного маршрута: точки, этапы, описание, GPX-треки и материалы поездки.";

    return {
        title,
        description,
        canonicalPath: pathname,
    };
};

const ROUTE_SEO_RULES = [
    {
        path: "/",
        end: true,
        getMeta: () => ({
            title: "Планировщик путешествий и маршрутов | TrackMe",
            description:
                "TrackMe — сервис для создания маршрутов и планов поездок с бюджетом, списками вещей, GPX-треками, файлами и участниками.",
            canonicalPath: "/",
            jsonLd: [ORGANIZATION_JSON_LD, WEBSITE_JSON_LD],
        }),
    },
    {
        path: "/planirovanie-puteshestviy/*",
        end: false,
        getMeta: () => ({
            title: "Приложение для планирования путешествий и маршрутов | TrackMe",
            description:
                "Пошагово спланируйте путешествие в TrackMe: соберите маршрут, рассчитайте бюджет, подготовьте вещи, добавьте GPX-треки и пригласите участников.",
            canonicalPath: "/planirovanie-puteshestviy/",
            jsonLd: PLANNING_FAQ_JSON_LD,
        }),
    },
    {
        path: "/o-trackme/",
        end: true,
        getMeta: () => ({
            title: "О TrackMe — планировщике путешествий и маршрутов",
            description: "История TrackMe: как учебное задание на курсах программирования выросло в полноценный сервис для планирования путешествий, маршрутов и расходов.",
            canonicalPath: "/o-trackme/",
            jsonLd: [
                ORGANIZATION_JSON_LD,
                {
                    "@context": "https://schema.org",
                    "@type": "SoftwareApplication",
                    name: "TrackMe — планировщик путешествий",
                    url: "https://trackme.su/",
                    applicationCategory: "TravelApplication",
                    operatingSystem: "Web, Android, iOS",
                    publisher: { "@id": "https://trackme.su/#organization" },
                },
                buildBreadcrumbJsonLd([
                    { name: "Главная", path: "/" },
                    { name: "О TrackMe", path: "/o-trackme/" },
                ]),
            ],
        }),
    },
    {
        path: "/guide/",
        end: true,
        getMeta: () => ({
            title: "Инструкция по TrackMe — как спланировать путешествие",
            description: "Пошаговое руководство: описание поездки, план по дням, карта, бюджет, снаряжение, файлы и совместное планирование в TrackMe.",
            canonicalPath: "/guide/",
        }),
    },
    {
        path: "/oferta/",
        end: true,
        getMeta: ({ searchParams }) => {
            const doc = (searchParams.get("doc") || "oferta").toLowerCase();
            const docMeta = LEGAL_DOC_META[doc] || LEGAL_DOC_META.oferta;

            return {
                title: docMeta.title,
                description: docMeta.description,
                canonicalPath: `/oferta/?doc=${LEGAL_DOC_META[doc] ? doc : "oferta"}`,
            };
        },
    },
    {
        path: "/policy/",
        end: true,
        getMeta: () => ({
            title: "Политика конфиденциальности TrackMe",
            description: "Подробная политика конфиденциальности сервиса TrackMe.",
            canonicalPath: "/policy/",
        }),
    },
    {
        path: "/cookie-policy/",
        end: true,
        getMeta: () => ({
            title: "Политика Cookie TrackMe",
            description: "Условия использования Cookie и аналогичных технологий на сайте TrackMe.",
            canonicalPath: "/cookie-policy/",
        }),
    },
    {
        path: "/gotovye-marshruty/",
        end: true,
        getMeta: ({ searchParams }) => {
          const page = Math.max(1, Number.parseInt(searchParams.get("page") || "1", 10) || 1);
          const hasFilters = Array.from(searchParams.keys()).some((key) => key !== "page");
          const canonicalPath = `/gotovye-marshruty/${page > 1 ? `?page=${page}` : ""}`;
          const canonicalUrl = new URL(canonicalPath, "https://trackme.su").toString();
          return {
            title: "Готовые маршруты путешествий с картой | TrackMe",
            description: "Выбирайте готовые маршруты путешествий: фильтруйте по стране, региону, типу, длительности и сложности, изучайте фотографии и точки на карте.",
            canonicalPath,
            robots: hasFilters ? NOINDEX_ROBOTS : DEFAULT_ROBOTS,
            openGraph: {
                image: PUBLIC_ROUTES_SOCIAL_IMAGE,
                imageAlt: "TrackMe — готовые маршруты путешествий",
                imageWidth: "1200",
                imageHeight: "630",
                imageType: "image/jpeg",
            },
            jsonLd: [
              { ...CATALOG_COLLECTION_JSON_LD, "@id": `${canonicalUrl}#collection`, url: canonicalUrl },
              {
                ...CATALOG_BREADCRUMBS_JSON_LD,
                "@id": `${canonicalUrl}#breadcrumbs`,
                itemListElement: CATALOG_BREADCRUMBS_JSON_LD.itemListElement.map((item, index) => (
                  index === 1 ? { ...item, item: canonicalUrl } : item
                )),
              },
            ],
          };
        },
    },
    {
        path: "/marshruty/:routeRegion/:travelSlug/",
        end: true,
        getMeta: (ctx) => buildPublicTravelFallbackMeta(ctx),
    },
    {
        path: "/publictravelplans/:travelPlanId/:travelSlug/",
        end: true,
        getMeta: (ctx) => ({ ...buildPublicTravelFallbackMeta(ctx), robots: NOINDEX_ROBOTS }),
    },
    {
        path: "/publictravelplans/:travelIdentifier",
        end: true,
        getMeta: (ctx) => ({
            ...buildPublicTravelFallbackMeta(ctx),
            robots: NOINDEX_ROBOTS,
        }),
    },
    {
        path: "/travel/shared/:token",
        end: true,
        getMeta: ({ pathname }) => ({
            title: "Совместный доступ к путешествию | TrackMe",
            description: "Просмотр маршрута, бюджета, снаряжения и файлов по ссылке совместного доступа.",
            canonicalPath: pathname,
        }),
    },
    {
        path: "/booking/",
        end: true,
        getMeta: () => ({
            title: "Сервисы для путешествий: билеты, жильё и трансферы | TrackMe",
            description: "Партнёрские сервисы для поиска авиабилетов, поездов, автобусов, жилья, экскурсий, аренды автомобилей и трансферов для путешествий.",
            canonicalPath: "/booking/",
            openGraph: {
                type: "website",
                image: "/images/01_appbase/fon/fon_1.webp",
                imageAlt: "Путешествие на парусной яхте у скалистого побережья",
            },
            jsonLd: BOOKING_BREADCRUMBS_JSON_LD,
        }),
    },
    {
        path: "/userlogin",
        end: true,
        getMeta: () => ({
            title: "Вход в TrackMe",
            description: "Войдите в аккаунт TrackMe, чтобы управлять маршрутами и планами путешествий.",
            canonicalPath: "/userlogin",
        }),
    },
    {
        path: "/register",
        end: true,
        getMeta: () => ({
            title: "Регистрация в TrackMe",
            description: "Создайте аккаунт TrackMe и начните планировать путешествия в одном приложении.",
            canonicalPath: "/register",
        }),
    },
    {
        path: "/createuser",
        end: true,
        getMeta: () => ({
            title: "Регистрация в TrackMe",
            description: "Создайте аккаунт TrackMe и начните планировать путешествия в одном приложении.",
            canonicalPath: "/register",
        }),
    },
    {
        path: "/signup",
        end: true,
        getMeta: () => ({
            title: "Регистрация в TrackMe",
            description: "Создайте аккаунт TrackMe и начните планировать путешествия в одном приложении.",
            canonicalPath: "/register",
        }),
    },
    {
        path: "/passwordreset",
        end: true,
        getMeta: () => ({
            title: "Восстановление пароля | TrackMe",
            description: "Запросите ссылку для восстановления доступа к аккаунту TrackMe.",
            canonicalPath: "/passwordreset",
        }),
    },
    {
        path: "/reset-password/:uid/:token",
        end: true,
        getMeta: ({ pathname }) => ({
            title: "Новый пароль | TrackMe",
            description: "Установите новый пароль для входа в аккаунт TrackMe.",
            canonicalPath: pathname,
        }),
    },
    {
        path: "/confirm-email/:token",
        end: true,
        getMeta: ({ pathname }) => ({
            title: "Подтверждение email | TrackMe",
            description: "Подтвердите адрес электронной почты для активации аккаунта TrackMe.",
            canonicalPath: pathname,
        }),
    },
    {
        path: "/kak-sostavit-plan-puteshestviya/*",
        end: false,
        getMeta: () => {
            const guide = getTravelPlanningGuide();
            return {
                title: guide.title,
                description: guide.description,
                canonicalPath: guide.path,
                openGraph: { type: "article", image: guide.screenshots.items[0].src, imageAlt: guide.screenshots.items[0].alt },
                jsonLd: getPlanningGuideJsonLd(guide),
            };
        },
    },
    {
        path: "/ii-planirovshchik-puteshestviy/*",
        end: false,
        getMeta: () => {
            const content = getAiTravelPlannerContent();
            return {
                title: content.title, description: content.description, canonicalPath: content.path,
                openGraph: { image: content.screenshots.items[0].src, imageAlt: content.screenshots.items[0].alt },
                jsonLd: getAiTravelPlannerJsonLd(content),
            };
        },
    },
    {
        path: "/chto-posmotret/:destinationSlug/",
        end: true,
        getMeta: ({ params }) => {
            const slug = String(params.destinationSlug || "").toLocaleLowerCase("ru-RU");
            const path = `/chto-posmotret/${slug}/`;
            return {
                title: "Путеводитель по направлению | TrackMe",
                description: "Редакторский путеводитель и готовые маршруты путешествий TrackMe.",
                canonicalPath: path,
            };
        },
    },

    {
        path: "/statistika-puteshestviy/*",
        end: false,
        getMeta: () => ({
            title: STATISTICS_TITLE,
            description: STATISTICS_DESCRIPTION,
            canonicalPath: "/statistika-puteshestviy/",
            jsonLd: buildContentPageJsonLd({
                title: STATISTICS_TITLE.replace(" | TrackMe", ""),
                description: STATISTICS_DESCRIPTION,
                path: "/statistika-puteshestviy/",
                faq: [
                    { question: "Все путешествия автоматически попадают в статистику?", answer: "Нет, учёт каждого путешествия в личной статистике можно включить или выключить отдельно." },
                    { question: "Почему часть показателей пустая?", answer: "Статистика строится из заполненных данных выбранных путешествий: географии, перемещений, проживания и фактических расходов." },
                    { question: "Можно ли посмотреть расходы по категориям и странам?", answer: "Да, в статистике есть отдельные диаграммы расходов по категориям и странам." },
                ],
            }),
        }),
    },
    {
        path: "/auth/vk/callback",
        end: true,
        getMeta: () => ({
            title: "Авторизация через VK | TrackMe",
            description: "Завершение безопасного входа в TrackMe через VK ID.",
            canonicalPath: "/auth/vk/callback",
        }),
    },
    {
        path: "/userlk/:userId/profile",
        end: true,
        getMeta: ({ params }) => ({
            title: "Профиль пользователя | TrackMe",
            description: "Личный профиль пользователя в TrackMe.",
            canonicalPath: `/userlk/${params.userId}/profile`,
        }),
    },
    {
        path: "/userlk/:userId/createtraveltrack",
        end: true,
        getMeta: ({ params }) => ({
            title: "Мои поездки | TrackMe",
            description: "Список ваших планов путешествий, маршрутов и поездок.",
            canonicalPath: `/userlk/${params.userId}/usertravelplans`,
        }),
    },
    {
        path: "/userlk/:userId/usertravelplans",
        end: true,
        getMeta: ({ params }) => ({
            title: "Мои поездки | TrackMe",
            description: "Список ваших планов путешествий, маршрутов и поездок.",
            canonicalPath: `/userlk/${params.userId}/usertravelplans`,
        }),
    },
    {
        path: "/userlk/:userId/usertravelplans/:travelPlanId/:travelSlug",
        end: true,
        getMeta: ({ pathname }) => ({
            title: "Карточка путешествия | TrackMe",
            description: "План путешествия: маршрут, бюджет, снаряжение, файлы и участники.",
            canonicalPath: pathname,
        }),
    },
    {
        path: "/userlk/:userId/usertravelplans/:travelSlug",
        end: true,
        getMeta: ({ pathname }) => ({
            title: "Карточка путешествия | TrackMe",
            description: "План путешествия: маршрут, бюджет, снаряжение, файлы и участники.",
            canonicalPath: pathname,
        }),
    },
    {
        path: "/userlk/:userId/usertravelmap",
        end: true,
        getMeta: ({ params }) => ({
            title: "Карта путешествия | TrackMe",
            description: "Интерактивная карта маршрутов, точек и треков вашего путешествия.",
            canonicalPath: `/userlk/${params.userId}/usertravelmap`,
        }),
    },
    {
        path: "/userlk/:userId/userstatistics",
        end: true,
        getMeta: ({ params }) => ({
            title: "Статистика путешествий | TrackMe",
            description: "Статистика поездок, дистанций, треков и расходов в вашем аккаунте.",
            canonicalPath: `/userlk/${params.userId}/userstatistics`,
        }),
    },
    {
        path: "/userlk/:userId/settings",
        end: true,
        getMeta: ({ params }) => ({
            title: "Настройки аккаунта | TrackMe",
            description: "Управление настройками аккаунта, уведомлениями и безопасностью.",
            canonicalPath: `/userlk/${params.userId}/settings`,
        }),
    },
    {
        path: "/userlk/:userId/useradmin",
        end: true,
        getMeta: ({ params }) => ({
            title: "Админ-панель | TrackMe",
            description: "Управление публичными маршрутами и данными в админ-разделе TrackMe.",
            canonicalPath: `/userlk/${params.userId}/useradmin`,
        }),
    },
    {
        path: "/userlk/:userId/useradmin/publictravelplans/:travelPlanId/:travelSlug",
        end: true,
        getMeta: ({ pathname }) => ({
            title: "Публичная карточка маршрута (админ) | TrackMe",
            description: "Административный просмотр публичной карточки маршрута.",
            canonicalPath: pathname,
        }),
    },
    {
        path: "/userlk/:userId/useradmin/publictravelplans/:travelIdentifier",
        end: true,
        getMeta: ({ pathname }) => ({
            title: "Публичная карточка маршрута (админ) | TrackMe",
            description: "Административный просмотр публичной карточки маршрута.",
            canonicalPath: pathname,
        }),
    },
    {
        path: "/error",
        end: true,
        getMeta: () => ({
            title: "Страница ошибки | TrackMe",
            description: "Страница ошибки или недоступного раздела в сервисе TrackMe.",
            canonicalPath: "/error",
        }),
    },
];

const buildCanonicalPath = (value) => {
    if (typeof value !== "string") return "/";
    const trimmed = value.trim();
    if (!trimmed) return "/";
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
};

const fallbackSeo = ({ pathname }) => ({
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    canonicalPath: pathname || "/",
    robots: NOINDEX_ROBOTS,
});

export const resolveSeoByLocation = ({ pathname, search = "" }) => {
    const searchParams = new URLSearchParams(search);

    for (const rule of ROUTE_SEO_RULES) {
        const match = matchPath(
            {
                path: rule.path,
                end: rule.end ?? true,
                caseSensitive: false,
            },
            pathname
        );

        if (!match) continue;

        const ruleMeta = rule.getMeta({
            pathname,
            params: match.params || {},
            searchParams,
        });
        return withSeoDefaults(
            {
                ...ruleMeta,
                canonicalPath: buildCanonicalPath(ruleMeta?.canonicalPath || pathname || "/"),
            },
            pathname
        );
    }

    const meta = fallbackSeo({ pathname });
    return withSeoDefaults(
        {
            ...meta,
            canonicalPath: buildCanonicalPath(meta.canonicalPath),
        },
        pathname
    );
};
